import { useEffect, useRef, useState } from 'react';

interface VideoPlayerProps {
  cover: string;
  title: string;
}

export default function VideoPlayer({ cover, title }: VideoPlayerProps) {
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!playing) {
      if (timerRef.current) window.clearInterval(timerRef.current);
      return;
    }
    timerRef.current = window.setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          if (timerRef.current) window.clearInterval(timerRef.current);
          return 100;
        }
        return prev + 0.6;
      });
    }, 500);
    return () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
    };
  }, [playing]);

  const togglePlay = () => {
    if (progress >= 100) {
      setProgress(0);
      setPlaying(true);
      return;
    }
    setPlaying((v) => !v);
  };

  const restart = () => {
    setProgress(0);
    setPlaying(true);
  };

  return (
    <div className="w-full">
      <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-background-950 border border-primary-900/50">
        <img
          src={cover}
          alt={`${title} 播放画面`}
          title={`${title} 在线观看`}
          className={`w-full h-full object-top object-cover transition-opacity duration-500 ${
            playing ? 'opacity-40' : 'opacity-100'
          }`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/80 via-background-950/20 to-background-950/40"></div>

        {playing && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
            <span className="flex items-end gap-1 h-10">
              <i className="ri-bar-chart-fill text-3xl text-accent-400 animate-pulse"></i>
              <span className="font-heading text-2xl text-foreground-100">正在播放（演示）</span>
            </span>
            <p className="text-xs text-foreground-400">这是前端模拟播放画面，用于演示播放器交互。</p>
          </div>
        )}

        {!playing && (
          <button
            type="button"
            onClick={togglePlay}
            aria-label="播放"
            className="absolute inset-0 flex items-center justify-center cursor-pointer group"
          >
            <span className="w-20 h-20 flex items-center justify-center rounded-full bg-accent-500/90 text-background-950 group-hover:scale-110 transition-transform">
              <i className="ri-play-fill text-4xl"></i>
            </span>
          </button>
        )}

        <div className="absolute bottom-0 left-0 right-0 px-4 pb-3 pt-8 bg-gradient-to-t from-background-950 to-transparent">
          <div className="w-full h-1 rounded-full bg-foreground-700/50 overflow-hidden">
            <div
              className="h-full bg-accent-500 transition-all duration-500"
              style={{ width: `${Math.min(progress, 100)}%` }}
            ></div>
          </div>
          <div className="mt-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? '暂停' : '播放'}
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-900/80 text-foreground-100 hover:bg-primary-500/40 transition-colors cursor-pointer"
              >
                <i className={playing ? 'ri-pause-fill text-lg' : 'ri-play-fill text-lg'}></i>
              </button>
              <button
                type="button"
                onClick={restart}
                aria-label="重新播放"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-background-900/80 text-foreground-100 hover:bg-primary-500/40 transition-colors cursor-pointer"
              >
                <i className="ri-restart-line text-lg"></i>
              </button>
              <span className="text-xs text-foreground-300">
                {Math.floor((progress / 100) * 24)}:00 / 24:05（演示）
              </span>
            </div>
            <span className="hidden sm:flex items-center gap-1 text-xs text-foreground-400">
              <i className="ri-moon-clear-line"></i>
              夜月画质
            </span>
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-start gap-2 rounded-md bg-accent-500/10 border border-accent-500/30 px-4 py-3">
        <i className="ri-alert-line text-accent-400 mt-0.5"></i>
        <p className="text-xs text-accent-200 leading-relaxed">
          演示用途，完整版视频待接入。当前播放区域为前端模拟效果，不代表真实流媒体播放。
        </p>
      </div>
    </div>
  );
}